import { observe } from 'mobx';
import { observer } from 'mobx-react';
import { Fragment, useEffect, useState } from 'react';

import DealershipChooser from 'components/DealershipChooser';
import PitstopSimpleForm from 'components/PitstopSimpleForm';
import PropTypes from 'prop-types';
import { PitstopButton } from 'shared';
import { AppStore, ShopStore, WorkOrderStore } from 'stores';
import styled from 'styled-components';
import { measurementUnits } from '../../shared/utils';
import {
  AutoComplete,
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Spin,
  Tooltip,
  Typography,
  Upload,
} from 'antd';
import { customFleets, webServiceProvider } from 'shared';

import {
  CalendarOutlined,
  CloseOutlined,
  DeleteOutlined,
  DollarOutlined,
  InboxOutlined,
  InfoCircleOutlined,
  LeftOutlined,
  PlusOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { Logger } from 'stores/Classes';

import SubLocationDropdown from './SubLocationDropdown';

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 25px;
`;

const Wrapper = styled.div`
  position: relative;

  .hide-label {
    label {
      visibility: hidden;
    }
  }
`;

const NoImageWrapper = styled.div`
  width: 100%;
  height: 100%;
  background-color: #f0f0f0;
  border-radius: 5px;
  height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const ImageWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 215px;
  height: 215px;
  overflow: hidden;
  background-color: #f0f0f0;
`;

const UploaderWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-top: 20px;
  height: auto;

  .ant-upload-picture-card-wrapper {
    width: auto;
    margin-bottom: 20px;
  }

  .item {
    margin-right: 20px;
    margin-bottom: 20px;
    position: relative;
    display: flex;
    align-items: center;

    width: 100px;
    height: 100px;
    background-color: #d5d3d3;

    &.has-error {
      border: solid 2px red;
      border-style: dotted;
    }

    .remove-button {
      position: absolute;
      top: -11px;
      right: -10px;
      opacity: 1;
      background-color: #333333;
      color: white;

      &:hover {
        background-color: black;
        color: white;
        border-color: black;
      }
    }
  }

  .upload-list-inline .ant-upload-list-item .ant-upload-list-item-actions {
    top: 0;
    right: 60px;
    opacity: 1;

    a {
      color: red;
    }

    i {
      color: red;
    }
  }
`;

const ImageUploadWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  font-size: 13px;
  align-items: center;
  justify-content: center;
`;

// 10000000 = 10MB
// 1000000 = 1MB
const MAX_FILE_SIZE = 10000000;

const PartDetailsPage = (props) => {
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(true);
  const [purchaseHistoryData, setPurchaseHistoryData] = useState([]);
  const [shopParts, setShopParts] = useState([]);
  const [currentPart, setCurrentPart] = useState({});
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [fileList, setFileList] = useState([]);
  const [partImage, setPartImage] = useState(null);
  const [assetMakeDataSource, setAssetMakeDataSource] = useState([]);
  const [assetTypeDataSource, setAssetTypeDataSource] = useState([
    'Vehicle',
    'Trailer',
    'Misc.',
  ]);
  const [uploadErrorMessage, setUploadErrorMessage] = useState('');

  const purchaseHistoryColumns = [
    {
      title: 'Vendor',
      dataIndex: 'vendor',
      key: 'vendor',
      render: (vendor) => (
        <Select
          placeholder="Please select"
          value={vendor}
          style={{ width: 120 }}
        >
          <Option value="jack">
            <Fragment>
              <div style={{ fontWeight: 'bold' }}>Vendor #1</div>
              <span style={{ color: 'grey' }}>
                4512 3rd Ave Save Mart
                <br />
                Seattle, WA 98101
              </span>
            </Fragment>
          </Option>
          <Option value="lucy">
            <Fragment>
              <div style={{ fontWeight: 'bold' }}>Vendor #2</div>
              <span style={{ color: 'grey' }}>
                4512 3rd Ave Save Mart
                <br />
                Seattle, WA 98101
              </span>
            </Fragment>
          </Option>
          <Option value="jane">
            <Fragment>
              <div style={{ fontWeight: 'bold' }}>Vendor #2</div>
              <span style={{ color: 'grey' }}>
                4512 3rd Ave Save Mart
                <br />
                Seattle, WA 98101
              </span>
            </Fragment>
          </Option>
        </Select>
      ),
    },
    {
      title: 'PO Amount',
      dataIndex: 'poAmount',
      key: 'poAmount',
      render: () => (
        <Input
          type="number"
          prefix={<DollarOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
        />
      ),
    },
    {
      title: 'Number of Units',
      dataIndex: 'numberOfUnits',
      key: 'numberOfUnits',
      render: () => <Input type="number" />,
    },
    {
      title: 'Date Ordered',
      dataIndex: 'dateOrdered',
      key: 'dateOrdered',
      render: () => (
        <Input
          prefix={<CalendarOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
        />
      ),
    },
    {
      title: '',
      key: 'action',
      render: (text, record) => (
        <Fragment>
          <Tooltip title="Delete the Vendor">
            <PitstopButton
              icon={<DeleteOutlined />}
              danger
              onClick={() => onClickDeleteVendor(record)}
            />
          </Tooltip>
        </Fragment>
      ),
    },
  ];

  useEffect(() => {
    AppStore.setSelectedSidebarKey('/parts');
    getCurrentPartData();

    return () => {
      if (disposer) disposer();
    };
  }, []);

  const disposer =
    ShopStore.currentShop && ShopStore.currentShop.id
      ? observe(ShopStore.currentShop, 'id', async () => {
          if (ShopStore.isAdmin()) return;
          return getCurrentPartData();
        })
      : null;

  const getCurrentPartData = async () => {
    const parts = await loadSelectedShopParts(ShopStore.currentShop.id);
    setShopParts(parts);
    const partId = parseInt(props.match.params.id);

    const currentPart = parts.find((part) => part.id === partId) || {};
    setCurrentPart(currentPart);
    if (currentPart.images) {
      const images = currentPart.images.split(',');
      setFileList(
        images.map((image, index) => ({
          uid: index,
          name: 'image.png',
          status: 'done',
          url: image,
        }))
      );
      setPartImage(images[0]);
    } else {
      setFileList([]);
      setPartImage(null);
    }

    form.setFieldsValue({
      number: currentPart.number,
      description: currentPart.description,
      type: currentPart.type,
      make: currentPart.make,
      currentStock: currentPart.currentStock,
      stocklimit: currentPart.stocklimit,
      cost: currentPart.cost,
      warrantyStart: currentPart.warrantyStart,
      warrantyEnd: currentPart.warrantyEnd,
      vendor: currentPart.vendor,
      aisle: currentPart.aisle,
      shelf: currentPart.shelf,
      bin: currentPart.bin,
      subLocation: currentPart.sub_location,
      additionalNotes: currentPart.additionalNotes,
      volumeunit: currentPart.volumeunit,
    });
  };

  const loadSelectedShopParts = async (shopId) => {
    try {
      setLoading(true);
      const parts = await WorkOrderStore.getShopParts(shopId);
      return parts;
    } catch (error) {
      Logger.error(error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const navigateToList = () => {
    props.history.push('/parts');
  };

  const updateImages = async (partId) => {
    AppStore.addLoading('Updating images...');
    // get the difference between the current images and the new images
    const currentImages = currentPart.images
      ? currentPart.images.split(',')
      : [];
    const newImages = fileList.map((file) => file.originFileObj || file.url);
    const imagesToDelete = currentImages.filter(
      (image) => !newImages.includes(image)
    );

    // delete the images that are no longer in the list
    if (imagesToDelete.length > 0) {
      await Promise.all(
        imagesToDelete.map(async (image) => {
          try {
            await webServiceProvider.delete(
              `dashboard/v1/deleteimage?imageUrl=${image}`
            );
          } catch {
            AppStore.addError(`Image ${image} deleted failed!`);
          }
        })
      );
    }

    // upload the new images
    const nonDuplicatedImages = newImages.filter(
      (image) => !currentImages.includes(image)
    );
    const duplicatedImages = newImages.filter((image) =>
      currentImages.includes(image)
    );
    const partImages = [...duplicatedImages];
    if (nonDuplicatedImages.length > 0) {
      for (const image of nonDuplicatedImages) {
        const fd = new FormData();
        if (image instanceof File) {
          fd.append('singleInputFileName', image);
        } else {
          // create a File object from the url
          const response = await fetch(image);
          const blob = await response.blob();
          const file = new File([blob], 'image.png', { type: 'image/png' });
          fd.append('singleInputFileName', file);
        }
        try {
          const data = await webServiceProvider.postFormData(
            'dashboard/v1/uploadimage',
            fd
          );
          if (data.fileUrl) {
            partImages.push(data.fileUrl);
          }
        } catch {
          AppStore.addError(`Image ${fileList[0].name} uploaded failed!`);
        }
      }
    }
    AppStore.removeLoading('Updating images...');
    AppStore.addLoading('Updating part...');
    try {
      const formattedImages = partImages.join(',');
      await WorkOrderStore.updatePartOnCustomEndpoint({
        id: partId,
        images: formattedImages || null,
      });
    } catch {
      AppStore.addError('Failed to update part with images.');
    } finally {
      AppStore.removeLoading('Updating part...');
    }
    return Promise.resolve();
  };

  const onSubmit = async () => {
    try {
      console.log('onSubmit $$');

      const values = await form.validateFields();

      const transformedValues = {
        ...values,
        sub_location: values.subLocation,
      };

      const tooBigImages = fileList.filter(
        (file) => file.size && file.size > MAX_FILE_SIZE
      );

      if (tooBigImages.length > 0) {
        AppStore.addError('Image too big. Must be less than 10MB.');
        return;
      }

      const partId = props.match.params.id;
      await WorkOrderStore.updatePartOnCustomEndpoint({
        id: partId,
        ...transformedValues,
      });

      AppStore.addSuccess('The part was saved!');
      await updateImages(partId);
      props.history.push('/parts');
    } catch (error) {
      Logger.error(error);
    }
  };

  const onDelete = async () => {
    try {
      const partId = props.match.params.id;
      await WorkOrderStore.deletePartOnCustomEndpoint(partId);
      AppStore.addSuccess('The part was deleted!');
      props.history.push('/parts');
    } catch (error) {
      Logger.error(error);
    }
  };

  const onAddVendor = () => {
    const newData = {
      key: purchaseHistoryData.length + 1,
      vendor: null,
      poAmount: '',
      numberOfUnits: 1,
      dateOrdered: '',
    };
    setPurchaseHistoryData([...purchaseHistoryData, newData]);
  };

  const onClickDeleteVendor = (record) => {
    const newData = purchaseHistoryData.filter(
      (item) => item.key !== record.key
    );
    setPurchaseHistoryData(newData);
  };

  const getBase64 = (file) => {
    return new Promise((resolve, reject) => {
      // if file not blob type, resolve with file
      if (!(file instanceof Blob)) {
        return resolve(file);
      }
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleCancel = () => setPreviewVisible(false);

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }

    setPreviewImage(file.url || file.preview);
    setPreviewVisible(true);
  };

  const handleChange = async ({ fileList: newFileList }) => {
    setFileList(newFileList);

    if (newFileList.length > 0) {
      // check each image to see if the size is too big
      const tooBigImages = newFileList.filter(
        (file) => file.size > MAX_FILE_SIZE
      );

      if (tooBigImages.length) {
        // remove the image from the list
        setFileList(newFileList.filter((file) => file.size <= MAX_FILE_SIZE));
        setUploadErrorMessage('Image too big. Must be less than 10MB.');
      } else {
        setUploadErrorMessage('');
        const base64Image = await getBase64(
          newFileList[0].originFileObj || newFileList[0].url
        );
        setPartImage(base64Image);
      }
    } else {
      setPartImage(null);
    }
  };

  const handleRemove = (event, fileUid) => {
    event.stopPropagation();
    const newFileList = fileList.filter((file) => file.uid !== fileUid);
    setFileList(newFileList);

    handleChange({
      fileList: newFileList,
    });
  };

  return (
    <Fragment>
      <Wrapper>
        <div style={{ position: 'absolute', right: '0px' }}>
          <PitstopButton
            type="default"
            icon={<LeftOutlined />}
            onClick={navigateToList}
          >
            Back to Parts
          </PitstopButton>
        </div>
        <h1>Part Details</h1>

        <DealershipChooser />

        {/* If loading state is true, show a loading indicator */}
        {loading && (
          <LoadingContainer>
            <Spin />
          </LoadingContainer>
        )}

        <Row gutter={8}>
          <Col offset={3} span={14}>
            <PitstopSimpleForm
              form={form}
              submitLabel="Save Part"
              deleteLabel="Delete Part"
              onSubmit={onSubmit}
              onDelete={onDelete}
              layout="vertical"
            >
              <Row gutter={16}>
                <Col span={16}>
                  <Form.Item
                    label="Part Number"
                    name="number"
                    help={
                      customFleets.ufpFleet.includes(
                        ShopStore.currentShop.parentShopId ||
                          ShopStore.currentShop.id
                      )
                        ? 'Internal part identifier. Must be unique per location.'
                        : 'Internal part identifier. Must be unique per part.'
                    }
                    rules={[
                      {
                        required: true,
                        message: 'Please input the Part Number!',
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item
                    name="description"
                    label="Part Description"
                    rules={[
                      {
                        required: true,
                        message: 'Description is required.',
                      },
                      {
                        max: 255,
                        message:
                          'The description needs to be less than 255 characters.',
                      },
                    ]}
                  >
                    <TextArea rows={2} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  {partImage ? (
                    <ImageWrapper>
                      <img
                        src={partImage}
                        alt="part"
                        style={{
                          width: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain',
                        }}
                      />
                    </ImageWrapper>
                  ) : (
                    <NoImageWrapper>
                      <InboxOutlined
                        style={{ fontSize: '50px', color: 'lightgrey' }}
                      />
                      <div style={{ color: 'lightgrey' }}>No images added</div>
                    </NoImageWrapper>
                  )}
                </Col>
              </Row>

              {/* Asset type and make */}
              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item label="Asset Type" name="type">
                    <AutoComplete
                      style={{ width: '100%' }}
                      options={assetTypeDataSource?.map((i) => ({ value: i }))}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="make" label="Asset Make">
                    <AutoComplete
                      options={assetMakeDataSource?.map((i) => ({ value: i }))}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={8}>
                <Col span={6}>
                  <Form.Item
                    name="currentStock"
                    label="Current Stock"
                    rules={[
                      {
                        required: true,
                        message: 'Current stock is required.',
                      },
                      // don't allow negative numbers
                      {
                        validator: (_, value) => {
                          if (value < 0) {
                            return Promise.reject('Stock cannot be negative.');
                          }
                          return Promise.resolve();
                        },
                      },
                      // don't allow decimals
                      {
                        validator: (_, value) => {
                          if (value % 1 !== 0) {
                            return Promise.reject('Stock cannot be a decimal.');
                          }
                          return Promise.resolve();
                        },
                      },
                      {
                        validator: (_, value) => {
                          const stockLimit = form.getFieldValue('stocklimit');
                          if (
                            stockLimit !== undefined &&
                            value !== undefined &&
                            value > stockLimit
                          ) {
                            return Promise.reject(
                              'Current stock cannot be greater than stock limit.'
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      prefix={
                        <StopOutlined style={{ color: 'rgba(0,0,0,.25)' }} />
                      }
                      onChange={() => {
                        form.validateFields(['stocklimit']);
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name="volumeunit"
                    label="Meas. Unit"
                    placeholder="-"
                    value={currentPart.volumeunit}
                    onChange={(val) => {
                      form.setFieldsValue({ volumeunit: val });
                    }}
                  >
                    <Select style={{ width: '100%' }}>
                      <Select.Option value="">-</Select.Option>
                      {measurementUnits.map((unit) => (
                        <Select.Option key={unit} value={unit}>
                          {unit}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={6}>
                  <Form.Item
                    label="Stock Limit"
                    name="stocklimit"
                    rules={[
                      {
                        required: true,
                        message: 'Stock limit is required.',
                      },
                      // don't allow negative numbers
                      {
                        validator: (_, value) => {
                          console.log(_);
                          if (value < 0) {
                            return Promise.reject(
                              'Stock limit cannot be negative.'
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                      // don't allow decimals
                      {
                        validator: (_, value) => {
                          if (value % 1 !== 0) {
                            return Promise.reject(
                              'Stock limit cannot be a decimal.'
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                      {
                        validator: (_, value) => {
                          const currentStock =
                            form.getFieldValue('currentStock');
                          if (
                            currentStock !== undefined &&
                            value !== undefined &&
                            currentStock > value
                          ) {
                            return Promise.reject(
                              'Current stock cannot be greater than stock limit.'
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      prefix={
                        <StopOutlined style={{ color: 'rgba(0,0,0,.25)' }} />
                      }
                      onChange={() => {
                        form.validateFields(['currentStock']);
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Unit Cost" name="cost">
                    <Input
                      type="number"
                      prefix={
                        <DollarOutlined style={{ color: 'rgba(0,0,0,.25)' }} />
                      }
                    />
                  </Form.Item>
                </Col>
              </Row>

              {ShopStore.currentShop.id === 374 && (
                <Row gutter={8}>
                  <Col span={8}>
                    <Form.Item label="Warranty Start" name="warrantyStart">
                      <DatePicker
                        format={'MM/DD/YYYY'}
                        style={{ width: '100%' }}
                        showTime
                        placeholder="Select start date"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="Warranty End" name="warrantyEnd">
                      <DatePicker
                        format={'MM/DD/YYYY'}
                        style={{ width: '100%' }}
                        showTime
                        placeholder="Select end date"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="Vendor" name="vendor">
                      <Input type="text" />
                    </Form.Item>
                  </Col>
                </Row>
              )}

              <Row gutter={8}>
                <Col span={8}>
                  <Form.Item label="Aisle / Shelf / Bin" name="aisle">
                    <Input type="text" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item className="hide-label" name="shelf" label=" ">
                    <Input type="text" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item className="hide-label" name="bin" label=" ">
                    <Input type="text" />
                  </Form.Item>
                </Col>
              </Row>

              {customFleets.ufpFleet.includes(
                ShopStore.currentShop.parentShopId || ShopStore.currentShop.id
              ) && (
                <Row gutter={8}>
                  <Col span={8}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Form.Item label="Sub-location" name="subLocation">
                        <SubLocationDropdown
                          shopId={ShopStore.currentShop.id}
                          onChange={(value) => {
                            form.setFieldsValue({ subLocation: value });
                          }}
                        />
                      </Form.Item>
                      <Tooltip title="Add sub-locations for your parts based on your Samsara tagging structure">
                        <InfoCircleOutlined
                          style={{ marginLeft: 8, color: '#1890ff' }}
                        />
                      </Tooltip>
                    </div>
                  </Col>
                </Row>
              )}

              <Row gutter={8}>
                <Col span={24}>
                  <Form.Item label="Additional Notes:" name="additionalNotes">
                    <TextArea rows={4} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={8}>
                <Col span={24}>
                  <Text style={{ marginBottom: 16 }}>
                    Add images (the first image uploaded will be used for the
                    preview up above):
                  </Text>
                  <UploaderWrapper>
                    {fileList.map((file) => {
                      const base64Image = file.originFileObj
                        ? URL.createObjectURL(file.originFileObj)
                        : file.url;
                      return (
                        <div
                          key={file.uid}
                          className={
                            file.status === 'error' ? 'item has-error' : 'item'
                          }
                        >
                          <img
                            src={base64Image}
                            alt="part"
                            style={{
                              width: '100%',
                              maxHeight: '100%',
                              objectFit: 'contain',
                            }}
                          />
                          <Button
                            className="remove-button"
                            size="small"
                            onClick={(event) => handleRemove(event, file.uid)}
                            shape="circle"
                            icon={<CloseOutlined />}
                          />
                        </div>
                      );
                    })}
                    <Upload
                      accept="image/*"
                      listType="picture-card"
                      fileList={fileList}
                      onPreview={handlePreview}
                      onChange={handleChange}
                      beforeUpload={() => {
                        /* update state here */
                        return false;
                      }}
                      className="upload-list-inline"
                      showUploadList={false}
                    >
                      <ImageUploadWrapper>
                        <PlusOutlined style={{ fontSize: 13 }} />
                        Upload
                      </ImageUploadWrapper>
                    </Upload>
                    {uploadErrorMessage && (
                      <div style={{ color: 'red' }}>{uploadErrorMessage}</div>
                    )}
                  </UploaderWrapper>
                  <Modal
                    open={previewVisible}
                    footer={null}
                    onCancel={handleCancel}
                  >
                    <img
                      alt="example"
                      style={{ width: '100%' }}
                      src={previewImage}
                    />
                  </Modal>
                </Col>
              </Row>
            </PitstopSimpleForm>
          </Col>
        </Row>
      </Wrapper>
    </Fragment>
  );
};

PartDetailsPage.propTypes = {
  history: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
};

export default observer(PartDetailsPage);
